export type ErrorPayload =
  | string
  | {
      message?: string;
      mensagem?: string;
      error?: string;
      title?: string;
      detail?: string;
      details?: string;
      errors?: Record<string, string[] | string>;
    };

const extractMessage = (payload: ErrorPayload): string | null => {
  if (!payload) return null;
  if (typeof payload === 'string') return payload;

  if (payload.errors) {
    const messages = Object.values(payload.errors)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter(Boolean);
    if (messages.length) return messages.join('. ');
  }

  return (
    payload.message ||
    payload.mensagem ||
    payload.error ||
    payload.title ||
    payload.detail ||
    payload.details ||
    null
  );
};

const defaultStatusMessage = (status: number): string => {
  if (status === 400) return 'Requisição inválida';
  if (status === 401) return 'Não autorizado';
  if (status === 403) return 'Acesso negado';
  if (status === 404) return 'Recurso não encontrado';
  if (status === 500) return 'Erro interno do servidor';
  return `Erro ${status}`;
};

export const parseErrorResponse = async (
  response: Response,
  fallback?: string
): Promise<{ message: string; payload?: ErrorPayload; rawText?: string }> => {
  const text = await response.text().catch(() => '');
  if (!text) {
    return {
      message: fallback || defaultStatusMessage(response.status),
    };
  }

  try {
    const payload = JSON.parse(text) as ErrorPayload;
    return {
      message: extractMessage(payload) || fallback || defaultStatusMessage(response.status),
      payload,
    };
  } catch {
    return {
      message: text || fallback || defaultStatusMessage(response.status),
      rawText: text,
    };
  }
};

export const getErrorMessage = async (
  response: Response,
  fallback?: string
): Promise<string> => {
  const parsed = await parseErrorResponse(response, fallback);
  return parsed.message;
};
